/**
 * Client-side chat transcript export (verbatim, no LLM/backend) — plain text (.txt).
 * FREE plans get the wisetree.ai promo footer; paid plans pass branded=false.
 */
import type { ChatSession } from '../components/chatHome/types';

export const WATERMARK = 'Generated with Wisetree AI · wisetree.ai';

type TranscriptInput = { title: string; messages: Array<{ authorName: string; content: string }> };

/** Plain-text transcript (used directly for .txt and as the PDF source). */
export function transcriptText(session: TranscriptInput, opts: { branded?: boolean } = {}): string {
  const title = session.title || 'Chat';
  const body = (session.messages || [])
    .filter((m) => m.content && m.content.trim())
    .map((m) => `${m.authorName}: ${m.content}`)
    .join('\n\n');
  const footer = opts.branded ? `\n\n— ${WATERMARK}` : '';
  return `${title}\n${'='.repeat(title.length)}\n\n${body}\n${footer}`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function fileBase(title: string): string {
  return (title || 'chat').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'chat';
}

/** Download the conversation transcript as a plain-text (.txt) file. */
export async function downloadTranscript(session: ChatSession, _format: 'txt' = 'txt', opts: { branded?: boolean } = {}): Promise<void> {
  const base = fileBase(session.title);
  downloadBlob(new Blob([transcriptText(session, opts)], { type: 'text/plain' }), `${base}.txt`);
}
